import React, { useState, useEffect } from 'react';

const ProjectCard = ({ project, onDragStart, onClick }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, project)}
    onClick={() => onClick(project)}
    className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-move mb-3"
  >
    <h3 className="font-semibold text-slate-800 mb-2">{project.title}</h3>
    <p className="text-sm text-slate-600">👤 {project.client}</p>
    <p className="text-sm text-green-600 font-medium">💰 {project.price}원</p>
    <p className="text-xs text-slate-400">📅 {project.date}</p>
    {project.currentStage && (
      <div className="mt-2 text-xs">
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
          {project.currentStage}
        </span>
      </div>
    )}
  </div>
);

const ProjectManager = () => {
  const [projects, setProjects] = useState({
    '작업전': [],
    '작업중': [],
    '작업완료': []
  });

  const [draggedProject, setDraggedProject] = useState(null);
  const [draggedFrom, setDraggedFrom] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await fetch('/api/projects');
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('프로젝트 로드 실패:', error);
    }
  };

  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    const sourceColumn = Object.keys(projects).find(key =>
      projects[key].some(p => p.id === project.id)
    );
    setDraggedFrom(sourceColumn);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    
    if (!draggedProject || !draggedFrom) return;
    if (draggedFrom === targetColumn) return;

    // "작업중"으로 이동 시 자동 개발 프로세스 시작
    if (targetColumn === '작업중' && draggedFrom !== '작업중') {
      const confirmed = window.confirm(
        `"${draggedProject.title}" 프로젝트의 자동 개발을 시작하시겠습니까?\n\n` +
        `진행 순서:\n` +
        `1. 계획 수립\n` +
        `2. 설계\n` +
        `3. 코딩\n` +
        `4. 리뷰\n` +
        `5. 수정\n` +
        `6. 검증 (100% 만족 시 완료, 아니면 재시작)`
      );

      if (!confirmed) {
        setDraggedProject(null);
        setDraggedFrom(null);
        return;
      }

      // 프로젝트 이동
      const newProjects = { ...projects };
      newProjects[draggedFrom] = newProjects[draggedFrom].filter(p => p.id !== draggedProject.id);
      
      const updatedProject = {
        ...draggedProject,
        currentStage: '계획 중...',
        startedAt: new Date().toISOString()
      };
      
      newProjects[targetColumn] = [...newProjects[targetColumn], updatedProject];
      setProjects(newProjects);
      await saveProjects(newProjects);

      // 자동 개발 프로세스 시작
      await startAutoDevelopment(updatedProject);
    } else {
      // 일반 이동
      const newProjects = { ...projects };
      newProjects[draggedFrom] = newProjects[draggedFrom].filter(p => p.id !== draggedProject.id);
      newProjects[targetColumn] = [...newProjects[targetColumn], draggedProject];
      setProjects(newProjects);
      await saveProjects(newProjects);
    }

    setDraggedProject(null);
    setDraggedFrom(null);
  };

  const startAutoDevelopment = async (project) => {
    try {
      const response = await fetch('/api/projects/auto-develop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: project.id,
          title: project.title,
          client: project.client,
          requirements: project.requirements
        })
      });

      if (response.ok) {
        alert('자동 개발 프로세스가 시작되었습니다!\n백그라운드에서 진행되며, 완료되면 "작업완료"로 자동 이동됩니다.');
      }
    } catch (error) {
      console.error('자동 개발 시작 실패:', error);
      alert('자동 개발 시작에 실패했습니다.');
    }
  };

  const saveProjects = async (data) => {
    try {
      await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error('저장 실패:', error);
    }
  };

  const columnColors = {
    '작업전': { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-500' },
    '작업중': { bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-500' },
    '작업완료': { bg: 'bg-green-50', border: 'border-green-200', header: 'bg-green-500' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl">🚀</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">프로젝트 관리</h1>
                <p className="text-sm text-slate-500">작업중으로 드래그하면 자동 개발 시작</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-6">
          {Object.keys(projects).map((columnId) => (
            <div key={columnId} className="flex flex-col">
              <div className={`${columnColors[columnId].header} text-white px-4 py-3 rounded-t-xl font-semibold text-center shadow-md`}>
                {columnId} ({projects[columnId].length})
              </div>

              <div
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, columnId)}
                className={`${columnColors[columnId].bg} ${columnColors[columnId].border} border-2 rounded-b-xl p-3 min-h-[600px]`}
              >
                {projects[columnId].map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onDragStart={handleDragStart}
                    onClick={setSelectedProject}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedProject(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-slate-800 mb-4">{selectedProject.title}</h2>
            <div className="space-y-3 text-slate-600">
              <p><strong>고객:</strong> {selectedProject.client}</p>
              <p><strong>금액:</strong> {selectedProject.price}원</p>
              <p><strong>등록일:</strong> {selectedProject.date}</p>
              {selectedProject.currentStage && (
                <p><strong>현재 단계:</strong> {selectedProject.currentStage}</p>
              )}
              <div>
                <strong>요구사항:</strong>
                <p className="mt-2 p-4 bg-slate-50 rounded-lg whitespace-pre-wrap">
                  {selectedProject.requirements || '없음'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedProject(null)}
              className="mt-6 w-full py-2.5 bg-slate-200 hover:bg-slate-300 rounded-lg font-medium transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManager;
